// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import gitVersion from '../environments/git-version';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
// import V1 from './pages/v1';
// import V2 from './pages/v2';
import { Provider } from 'react-redux';
import store from './store';
import { DEFAULT_COMPONENT_ICONS } from '@azure/communication-react';
import { initializeIcons, registerIcons } from '@fluentui/react';
import { ConfigProvider } from './contexts/ConfigContext';
import InterviewHostAgent from './pages/AgentPage/InterviewHostAgent';
import JobsRoles from './pages/AgentPage/InterviewHostAgent/JobsRoles';
import AddCandidate from './pages/AgentPage/InterviewHostAgent/AddCandidate';
import Candidates from './pages/AgentPage/InterviewHostAgent/Candidates';
import InterviewReport from './pages/AgentPage/InterviewHostAgent/InterviewReport';
import SkillAssessmentReport from './pages/AgentPage/InterviewHostAgent/SkillAssessmentReport';
import Interview from './pages/Interview';
import LandingPage from './pages/LandingPage';
import Loader from './components/Loader';
import NotFound from './pages/NotFound';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';

initializeIcons();
registerIcons({ icons: DEFAULT_COMPONENT_ICONS });

// Legacy redirect component - currently commented out as these routes are not in use
// const LegacyRecruiterRedirect = (): JSX.Element => {
//   const location = useLocation();
//   const path = location.pathname.replace('/app/v1/interview-host', '').replace('/app/interview-host', '');
//   const nextPath = path || '/';

//   return <Navigate to={`${nextPath}${location.search}`} replace />;
// };

const ProtectedRoute = ({ children, activeNav }: { children: JSX.Element; activeNav?: string }): JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState<number | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const response = await fetch('/api/sso/status', { credentials: 'include' });
        const data: { isAuthenticated: boolean; idleTimeoutMs?: number } = await response.json();

        if (!data.isAuthenticated) {
          navigate('/login', { state: { from: location.pathname }, replace: true });
        } else {
          if (data.idleTimeoutMs && data.idleTimeoutMs > 0) setIdleTimeoutMs(data.idleTimeoutMs);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login', { state: { from: location.pathname }, replace: true });
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return <Loader />;
  }

  return (
    <AuthenticatedLayout activeNav={activeNav} idleTimeoutMs={idleTimeoutMs}>
      {children}
    </AuthenticatedLayout>
  );
};

const LoginPageWrapper = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const response = await fetch('/api/sso/status', { credentials: 'include' });
        const data: { isAuthenticated: boolean } = await response.json();

        if (data.isAuthenticated) {
          const params = new URLSearchParams(location.search);
          const returnTo = params.get('returnTo') || (location.state as { from?: string })?.from || '/';
          navigate(returnTo, { replace: true });
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    };

    checkAuth();
  }, [navigate, location]);

  const handleLogin = (): void => {
    setIsAuthenticating(true);
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo') || (location.state as { from?: string })?.from || '/';
    window.location.href = `/api/sso/start?redirectUri=${encodeURIComponent(returnTo)}`;
  };

  if (isAuthenticating) {
    return <Loader />;
  }

  return <LandingPage onLogin={handleLogin} />;
};

// Log SCM/build metadata on client startup
console.log(JSON.stringify(gitVersion));

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <ConfigProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPageWrapper />} />
            <Route
              path="/"
              element={
                <ProtectedRoute activeNav="home">
                  <InterviewHostAgent />
                </ProtectedRoute>
              }
            />
            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute activeNav="home">
                  <JobsRoles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId/candidates"
              element={
                <ProtectedRoute activeNav="home">
                  <Candidates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId/candidates/add"
              element={
                <ProtectedRoute activeNav="home">
                  <AddCandidate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId/candidates/:candidateId/report"
              element={
                <ProtectedRoute activeNav="home">
                  <InterviewReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId/candidates/:candidateId/skill-assessment-report"
              element={
                <ProtectedRoute activeNav="home">
                  <SkillAssessmentReport />
                </ProtectedRoute>
              }
            />
            {/* Legacy routes - commented out as they are not currently in use */}
            {/* <Route path="/app/interview-host/*" element={<LegacyRecruiterRedirect />} /> */}
            {/* <Route path="/app/v1/interview-host/*" element={<LegacyRecruiterRedirect />} /> */}
            {/* <Route path="/app/v1/*" element={<V1 />} /> */}
            {/* <Route path="/app/v2" element={<V2 />} /> */}
            {/* Interview flow - accepts token parameter - public route without sidebar */}
            <Route path="/app/interview/:token" element={<Interview />} />
            {/* 404 Not Found - catch all unmatched routes */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </BrowserRouter>
    </ConfigProvider>
  </Provider>
);
